import { Icons } from "@/shared/icons";
import { useMediaQuery } from "@/shared/hooks";
import { useLoadingStore } from "@/shared/stores/loadingStore";
import { useToastStore } from "@/shared/stores/toastStore";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormFlowStore } from "./formFlowStore";
import {
  FormFlowContainer,
  FormFlowContentPanel,
  FormFlowMobileStepButton,
  FormFlowMobileStepHeading,
  FormFlowMobileStepItem,
  FormFlowMobileStepLabel,
  FormFlowMobileStepMarker,
  FormFlowMobileStepPanel,
  FormFlowMobileStepPosition,
  FormFlowMobileStepper,
  FormFlowMobileStepTooltip,
  FormFlowStepButton,
  FormFlowStepIconSlot,
  FormFlowStepLabel,
  FormFlowStepTooltip,
  FormFlowStepper,
} from "./styles";
import type { FormFlowDefinition, FormFlowSaveResult } from "./types";
import { getStepDisplayIcon } from "./utils";
import { FormFlowStepShapeSvg } from "./FormFlowStepShapeSvg";
import { FormFlowStepSyncContext } from "./FormFlowStepSyncContext";

interface FormFlowProps<TState extends object, TContext> {
  instanceId: string;
  definition: FormFlowDefinition<TState, TContext>;
  context: TContext;
  onSaveSuccess?: (result: FormFlowSaveResult) => void;
}

const MOBILE_FORM_FLOW_QUERY = "(max-width: 767px)";
const DEFAULT_SAVE_ERROR_MESSAGE = "NO SE PUDO GUARDAR EL FORMULARIO";
const BLOCKED_STEP_TOOLTIP_TEXT =
  "Todos los campos son obligatorios, excepto los indicados como “Opcional”.";

const toRecord = (value: object): Record<string, unknown> => ({
  ...(value as Record<string, unknown>),
});

export const FormFlow = <TState extends object, TContext>({
  instanceId,
  definition,
  context,
  onSaveSuccess,
}: FormFlowProps<TState, TContext>) => {
  const initFlow = useFormFlowStore((state) => state.initFlow);
  const destroyFlow = useFormFlowStore((state) => state.destroyFlow);
  const goToStep = useFormFlowStore((state) => state.goToStep);
  const setStepData = useFormFlowStore((state) => state.setStepData);
  const setFormValue = useFormFlowStore((state) => state.setFormValue);
  const markStepCompleted = useFormFlowStore(
    (state) => state.markStepCompleted,
  );
  const submitStart = useFormFlowStore((state) => state.submitStart);
  const submitSuccess = useFormFlowStore((state) => state.submitSuccess);
  const submitError = useFormFlowStore((state) => state.submitError);
  const startLoading = useLoadingStore((state) => state.start);
  const stopLoading = useLoadingStore((state) => state.stop);
  const showToast = useToastStore((state) => state.showToast);
  const runtimeState = useFormFlowStore((state) => state.flows[instanceId]);
  const [hoveredStepId, setHoveredStepId] = useState<string | null>(null);
  const isMobile = useMediaQuery(MOBILE_FORM_FLOW_QUERY);
  const activeMobileStepButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousStepIndexRef = useRef(0);
  const pendingNavigationTargetRef = useRef<number | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    initFlow(instanceId, toRecord(definition.initialState));

    return () => {
      destroyFlow(instanceId);
    };
  }, [definition.initialState, destroyFlow, initFlow, instanceId]);

  const fallbackRuntimeState = useMemo(
    () => ({
      currentStepIndex: 0,
      formState: toRecord(definition.initialState),
      completedStepIds: [],
      dirtyStepIds: [],
      submitStatus: "idle" as const,
      isDirty: false,
    }),
    [definition.initialState],
  );
  const runtime = runtimeState ?? fallbackRuntimeState;
  const steps = definition.steps;
  const currentStepIndex = Math.min(
    runtime.currentStepIndex,
    Math.max(steps.length - 1, 0),
  );
  const currentStep = steps[currentStepIndex];
  const formState = runtime.formState as TState;
  const activeStepPanelId = currentStep
    ? `${instanceId}-${currentStep.id}-panel`
    : "";

  useEffect(() => {
    const hasChangedStep =
      previousStepIndexRef.current !== currentStepIndex;
    previousStepIndexRef.current = currentStepIndex;

    if (!isMobile || !hasChangedStep) {
      return;
    }

    const activeStepButton = activeMobileStepButtonRef.current;
    activeStepButton?.focus({ preventScroll: true });
    activeStepButton?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  }, [currentStepIndex, isMobile]);

  const isLocked =
    Boolean(definition.lockAfterSave) && runtime.submitStatus === "success";

  const canNavigateToStep = useCallback(
    (stepId: string, index: number) =>
      index === currentStepIndex ||
      (!isLocked && runtime.completedStepIds.includes(stepId)),
    [currentStepIndex, isLocked, runtime.completedStepIds],
  );

  const handleStepClick = (stepId: string, index: number) => {
    if (!canNavigateToStep(stepId, index)) {
      return;
    }

    if (index === currentStepIndex) {
      return;
    }

    if (runtime.dirtyStepIds.length > 0) {
      const submitButton = activeStepPanelId
        ? document
            .getElementById(activeStepPanelId)
            ?.querySelector<HTMLButtonElement>('button[type="submit"]')
        : undefined;

      if (!submitButton || submitButton.disabled) {
        return;
      }

      pendingNavigationTargetRef.current = index;
      submitButton.click();
      window.setTimeout(() => {
        if (pendingNavigationTargetRef.current === index) {
          pendingNavigationTargetRef.current = null;
        }
      }, 0);
      return;
    }

    goToStep(instanceId, index);
  };

  const goPrevious = useCallback(() => {
    if (currentStepIndex === 0 || isLocked) {
      return;
    }

    goToStep(instanceId, currentStepIndex - 1);
  }, [currentStepIndex, goToStep, instanceId, isLocked]);

  const syncCurrentStepData = useCallback(
    (data: unknown) => {
      if (!currentStep) {
        return;
      }

      setStepData(instanceId, currentStep.stateKey, data, currentStep.id);
    },
    [currentStep, instanceId, setStepData],
  );
  const stepSyncContextValue = useMemo(
    () => ({ syncCurrentStepData }),
    [syncCurrentStepData],
  );

  const notifySaveError = useCallback(
    (message: string) => {
      submitError(instanceId, message);
      showToast({
        type: "error",
        title: message,
        duration: 3000,
      });
    },
    [instanceId, showToast, submitError],
  );

  const handleNext = useCallback(
    async (validData: TState[keyof TState]) => {
      const step = steps[currentStepIndex];

      if (!step || runtime.submitStatus === "submitting" || isLocked) {
        return false;
      }

      const pendingNavigationTarget = pendingNavigationTargetRef.current;
      pendingNavigationTargetRef.current = null;

      setStepData(instanceId, step.stateKey, validData, step.id);
      markStepCompleted(instanceId, step.id);

      const latestState = useFormFlowStore.getState().flows[instanceId];
      const nextFormState = {
        ...(latestState?.formState ?? runtime.formState),
        [step.stateKey]: validData,
      } as TState;
      const isLastStep = currentStepIndex === steps.length - 1;

      if (!isLastStep) {
        goToStep(
          instanceId,
          pendingNavigationTarget ?? currentStepIndex + 1,
        );
        return true;
      }

      const loadingKey = `custom:form-flow-submit:${instanceId}` as const;

      submitStart(instanceId);
      startLoading(loadingKey);

      try {
        const result = await definition.saveAdapter({
          formState: nextFormState,
          context,
          instanceId,
          submittedAt: new Date().toISOString(),
        });

        if (result && !result.success) {
          notifySaveError(
            result.message ??
              definition.saveErrorMessage ??
              DEFAULT_SAVE_ERROR_MESSAGE,
          );
          return false;
        }

        submitSuccess(instanceId);

        // Si el usuario salió mientras se guardaba, no se notifica a una pantalla que ya no existe.
        if (isMountedRef.current) {
          onSaveSuccess?.(result ?? { success: true });
        }
        return true;
      } catch {
        notifySaveError(
          definition.saveErrorMessage ?? DEFAULT_SAVE_ERROR_MESSAGE,
        );
        return false;
      } finally {
        stopLoading(loadingKey);
      }
    },
    [
      context,
      currentStepIndex,
      definition,
      goToStep,
      instanceId,
      isLocked,
      markStepCompleted,
      notifySaveError,
      onSaveSuccess,
      runtime.formState,
      runtime.submitStatus,
      setStepData,
      steps,
      submitStart,
      submitSuccess,
      startLoading,
      stopLoading,
    ],
  );

  if (!currentStep) {
    return null;
  }

  const ActiveStep = currentStep.Component;
  const activeStepContent = (
    <FormFlowContentPanel id={activeStepPanelId}>
      <FormFlowStepSyncContext.Provider value={stepSyncContextValue}>
        <ActiveStep
          context={context}
          instanceId={instanceId}
          formState={formState}
          data={formState[currentStep.stateKey]}
          setData={(data) =>
            setStepData(instanceId, currentStep.stateKey, data, currentStep.id)
          }
          setFormValue={(key, value) => setFormValue(instanceId, key, value)}
          handleNext={handleNext}
          goPrevious={goPrevious}
          isFirstStep={currentStepIndex === 0}
          isLastStep={currentStepIndex === steps.length - 1}
          submitStatus={runtime.submitStatus}
          completedStepIds={runtime.completedStepIds}
        />
      </FormFlowStepSyncContext.Provider>
    </FormFlowContentPanel>
  );

  return (
    <FormFlowContainer>
      {isMobile ? (
        <FormFlowMobileStepper aria-label={`Pasos de ${definition.name}`}>
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            const isActive = index === currentStepIndex;
            const isCompleted = runtime.completedStepIds.includes(step.id);
            const isEnabled = canNavigateToStep(step.id, index);
            return (
              <FormFlowMobileStepItem
                key={step.id}
                $active={isActive}
                $completed={isCompleted}
                $isLast={isLast}
              >
                <FormFlowMobileStepHeading>
                  <FormFlowMobileStepTooltip
                    title={
                      isEnabled || isLocked ? "" : BLOCKED_STEP_TOOLTIP_TEXT
                    }
                    wrapperAs="span"
                  >
                    <FormFlowMobileStepButton
                      ref={isActive ? activeMobileStepButtonRef : undefined}
                      type="button"
                      $active={isActive}
                      aria-label={`${step.label}, paso ${index + 1} de ${steps.length}`}
                      aria-current={isActive ? "step" : undefined}
                      aria-disabled={!isEnabled}
                      aria-expanded={isActive}
                      aria-controls={isActive ? activeStepPanelId : undefined}
                      disabled={!isEnabled}
                      onClick={() => handleStepClick(step.id, index)}
                    >
                      <FormFlowMobileStepMarker aria-hidden="true">
                        <Icons
                          name={step.icon}
                          width={26}
                          height={26}
                          color="currentColor"
                        />
                      </FormFlowMobileStepMarker>
                      <FormFlowMobileStepLabel>
                        {step.label}
                      </FormFlowMobileStepLabel>
                      {isActive ? (
                        <FormFlowMobileStepPosition>
                          Paso {index + 1} de {steps.length}
                        </FormFlowMobileStepPosition>
                      ) : null}
                    </FormFlowMobileStepButton>
                  </FormFlowMobileStepTooltip>
                </FormFlowMobileStepHeading>

                {isActive && (
                  <FormFlowMobileStepPanel>
                    {activeStepContent}
                  </FormFlowMobileStepPanel>
                )}
              </FormFlowMobileStepItem>
            );
          })}
        </FormFlowMobileStepper>
      ) : (
        <>
          <FormFlowStepper aria-label={`Pasos de ${definition.name}`}>
            {steps.map((step, index) => {
              const isFirst = index === 0;
              const isLast = index === steps.length - 1;
              const isActive = index === currentStepIndex;
              const isCompleted = runtime.completedStepIds.includes(step.id);
              const isEnabled = canNavigateToStep(step.id, index);
              const isHovered = hoveredStepId === step.id;
              const displayIcon = getStepDisplayIcon({
                icon: step.icon,
                completedIcon: step.completedIcon,
                editableIcon: step.editableIcon,
                isActive,
                isCompleted,
                isHovered,
              });

              return (
                <FormFlowStepTooltip
                  key={step.id}
                  $isFirst={isFirst}
                  title={
                    isEnabled || isLocked ? "" : BLOCKED_STEP_TOOLTIP_TEXT
                  }
                  wrapperAs="span"
                >
                  <FormFlowStepButton
                    type="button"
                    $active={isActive}
                    $completed={isCompleted}
                    $isFirst={isFirst}
                    $isLast={isLast}
                    $stackIndex={steps.length - index}
                    aria-current={isActive ? "step" : undefined}
                    aria-disabled={!isEnabled}
                    disabled={!isEnabled}
                    onMouseEnter={() => {
                      if (isEnabled) {
                        setHoveredStepId(step.id);
                      }
                    }}
                    onMouseLeave={() => setHoveredStepId(null)}
                    onClick={() => handleStepClick(step.id, index)}
                  >
                    <FormFlowStepShapeSvg
                      index={index}
                      totalSteps={steps.length}
                    />
                    <FormFlowStepIconSlot aria-hidden="true">
                      <Icons
                        name={displayIcon}
                        width={32}
                        height={32}
                        color="currentColor"
                      />
                    </FormFlowStepIconSlot>
                    <FormFlowStepLabel>{step.label}</FormFlowStepLabel>
                  </FormFlowStepButton>
                </FormFlowStepTooltip>
              );
            })}
          </FormFlowStepper>

          {activeStepContent}
        </>
      )}
    </FormFlowContainer>
  );
};
